import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X, Cookie, HelpCircle, Settings, ShieldOff, Lock, Server, SlidersHorizontal, RefreshCw, Phone, Clock } from "lucide-react";
import "@/App.css";

const CookiePolicy = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToContact = () => {
    setMobileMenuOpen(false);
    navigate("/", { state: { scrollTo: "contact" } });
  };

  const handleMobileNavClick = (action) => {
    setMobileMenuOpen(false);
    if (typeof action === "function") action();
  };

  return (
    <div className="privacy-page" data-testid="cookie-page">
      {/* Navigation */}
      <nav className="nav" data-testid="navigation">
        <div className="nav-container">
          <div className="nav-logo" data-testid="nav-logo" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
            <span className="logo-text">Precision Prescribing</span>
          </div>
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn" aria-label="Toggle menu">
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <div className="nav-links">
            <button onClick={() => navigate("/clinical-areas")} className="nav-link" data-testid="nav-clinical-areas">Clinical Areas</button>
            <button onClick={() => navigate("/dispensing")} className="nav-link" data-testid="nav-dispensing">Dispensing</button>
            <button onClick={() => navigate("/#how-it-works")} className="nav-link" data-testid="nav-how-it-works">How It Works</button>
            <button onClick={() => navigate("/#about")} className="nav-link" data-testid="nav-about">About</button>
            <button onClick={scrollToContact} className="nav-link nav-cta" data-testid="nav-contact-cta">Request a Discussion</button>
          </div>
          <div className={`mobile-nav ${mobileMenuOpen ? "mobile-nav-open" : ""}`} data-testid="mobile-nav">
            <button onClick={() => handleMobileNavClick(() => navigate("/clinical-areas"))} className="mobile-nav-link" data-testid="mobile-nav-clinical-areas">Clinical Areas</button>
            <button onClick={() => handleMobileNavClick(() => navigate("/dispensing"))} className="mobile-nav-link" data-testid="mobile-nav-dispensing">Dispensing</button>
            <button onClick={() => handleMobileNavClick(() => navigate("/#how-it-works"))} className="mobile-nav-link" data-testid="mobile-nav-how-it-works">How It Works</button>
            <button onClick={() => handleMobileNavClick(() => navigate("/#about"))} className="mobile-nav-link" data-testid="mobile-nav-about">About</button>
            <button onClick={() => handleMobileNavClick(scrollToContact)} className="mobile-nav-link mobile-nav-cta" data-testid="mobile-nav-contact-cta">Request a Discussion</button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="privacy-hero" data-testid="cookie-hero">
        <div className="privacy-hero-inner">
          <span className="privacy-eyebrow" data-testid="cookie-eyebrow">Legal</span>
          <h1 className="privacy-title" data-testid="cookie-title">Cookie Policy</h1>
          <p className="privacy-subtitle" data-testid="cookie-subtitle">
            How Precision Prescribing uses cookies and similar technologies on this website.
          </p>
          <div className="privacy-meta" data-testid="cookie-meta">
            <span className="privacy-meta-item"><Clock size={16} /> Last updated: 15 July 2026</span>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="privacy-layout privacy-layout-single" data-testid="cookie-layout">
        <article className="privacy-article" data-testid="cookie-article">

          {/* 1. Introduction */}
          <section className="privacy-block" data-testid="cookie-section-introduction">
            <h2><Cookie size={22} className="privacy-h2-icon" /> 1. Introduction</h2>
            <p>This Cookie Policy explains how Precision Prescribing uses cookies and similar technologies on this website.</p>
            <div className="privacy-callout" data-testid="cookie-entity-card">
              <Cookie size={24} className="privacy-callout-icon" />
              <div>
                <p className="privacy-callout-title">Precision Prescribing</p>
                <p className="privacy-callout-sub">A trading name of PHARMKG LTD</p>
              </div>
            </div>
            <p>This website is used to provide information about Precision Prescribing&rsquo;s B2B pharmacist-led prescribing support services and to allow organisations to submit enquiries through our contact or request form.</p>
          </section>

          {/* 2. What Are Cookies? */}
          <section className="privacy-block" data-testid="cookie-section-what-are">
            <h2><HelpCircle size={22} className="privacy-h2-icon" /> 2. What Are Cookies?</h2>
            <p>Cookies are small text files that may be placed on your device when you visit a website.</p>
            <p>Cookies can help websites function properly, remember basic preferences, support security, enable forms to work correctly, and improve the overall browsing experience.</p>
            <div className="privacy-info-card">
              <p style={{ marginBottom: 0 }}>Similar technologies may include local storage, session storage, pixels, tags or other tools that help a website operate.</p>
            </div>
          </section>

          {/* 3. How We Currently Use Cookies */}
          <section className="privacy-block" data-testid="cookie-section-how-used">
            <h2><Settings size={22} className="privacy-h2-icon" /> 3. How We Currently Use Cookies</h2>
            <p>At present, this website only uses cookies or similar technologies that are necessary for the website to function properly.</p>
            <p>These may include cookies or technologies required for:</p>
            <ul className="privacy-list-grid">
              <li>Website security</li>
              <li>Basic website functionality</li>
              <li>Page loading and performance</li>
              <li>Contact or enquiry form operation</li>
              <li>Preventing spam or misuse</li>
              <li>Maintaining a secure browsing session</li>
              <li>Remembering essential technical preferences</li>
            </ul>
            <div className="privacy-info-card privacy-info-card-highlight">
              <p style={{ marginBottom: 0 }}>These are known as <strong>strictly necessary cookies</strong>.</p>
            </div>
          </section>

          {/* 4. Analytics, Advertising and Tracking Cookies */}
          <section className="privacy-block" data-testid="cookie-section-tracking">
            <h2><ShieldOff size={22} className="privacy-h2-icon" /> 4. Analytics, Advertising and Tracking Cookies</h2>
            <p>We do not currently use cookies for:</p>
            <ul className="privacy-list-grid">
              <li>Advertising</li>
              <li>Tracking pixels</li>
              <li>Behavioural profiling</li>
              <li>Targeted marketing</li>
              <li>Third-party advertising networks</li>
              <li>Cross-site tracking</li>
              <li>Non-essential analytics</li>
            </ul>
            <div className="privacy-info-card">
              <p style={{ marginBottom: 0 }}>If we introduce analytics, advertising cookies, tracking pixels or other non-essential cookies in the future, we will update this Cookie Policy and implement consent controls where required.</p>
            </div>
          </section>

          {/* 5. Essential Cookies */}
          <section className="privacy-block" data-testid="cookie-section-essential">
            <h2><Lock size={22} className="privacy-h2-icon" /> 5. Essential Cookies</h2>
            <p>Essential cookies are required for the website to operate correctly.</p>
            <p>Because these cookies are necessary for the website to function, they cannot usually be switched off through the website itself.</p>
            <div className="privacy-info-card privacy-info-card-warn">
              <p style={{ marginBottom: 0 }}>You may be able to block cookies through your browser settings, but this may affect how the website works and may prevent some parts of the website, such as enquiry forms, from functioning properly.</p>
            </div>
          </section>

          {/* 6. Third-Party Services */}
          <section className="privacy-block" data-testid="cookie-section-third-party">
            <h2><Server size={22} className="privacy-h2-icon" /> 6. Third-Party Services</h2>
            <p>This website may use third-party services that support hosting, security, form submission, website functionality or technical operation.</p>
            <p>Where these services use cookies or similar technologies, they should only be used for essential website operation unless otherwise stated.</p>
            <div className="privacy-info-card privacy-info-card-highlight">
              <p style={{ marginBottom: 0 }}><strong>We do not currently use third-party advertising or tracking cookies.</strong></p>
            </div>
          </section>

          {/* 7. Managing Cookies */}
          <section className="privacy-block" data-testid="cookie-section-managing">
            <h2><SlidersHorizontal size={22} className="privacy-h2-icon" /> 7. Managing Cookies</h2>
            <p>You can usually control or delete cookies through your browser settings.</p>
            <p>Most browsers allow you to:</p>
            <ul className="privacy-list-grid">
              <li>View cookies stored on your device</li>
              <li>Delete cookies</li>
              <li>Block cookies</li>
              <li>Block third-party cookies</li>
              <li>Set preferences for specific websites</li>
            </ul>
            <div className="privacy-info-card privacy-info-card-warn">
              <p style={{ marginBottom: 0 }}>Please note that blocking all cookies may affect the functionality of this website.</p>
            </div>
          </section>

          {/* 8. Changes */}
          <section className="privacy-block" data-testid="cookie-section-changes">
            <h2><RefreshCw size={22} className="privacy-h2-icon" /> 8. Changes to This Cookie Policy</h2>
            <p>We may update this Cookie Policy from time to time.</p>
            <p>If we change how cookies are used on this website, we will update this page and revise the &ldquo;Last updated&rdquo; date.</p>
            <p>If we introduce non-essential cookies in the future, such as analytics or advertising cookies, we will update the Cookie Policy and add consent controls where required.</p>
          </section>

          {/* 9. Contact */}
          <section className="privacy-block privacy-block-contact" data-testid="cookie-section-contact">
            <h2><Phone size={22} className="privacy-h2-icon" /> 9. Contact</h2>
            <p>For questions about this Cookie Policy, please contact:</p>

            <div className="privacy-contact-grid privacy-contact-grid-single">
              <div className="privacy-contact-card" data-testid="cookie-contact-card">
                <div className="privacy-contact-badge">Cookie Policy Enquiries</div>
                <p className="privacy-contact-name">Precision Prescribing</p>
                <a href="mailto:hello@precisionpx.com" className="privacy-contact-email" data-testid="cookie-contact-email">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="2" y="4" width="20" height="16" rx="2"/>
                    <path d="m22 7-10 5L2 7"/>
                  </svg>
                  hello@precisionpx.com
                </a>
              </div>
            </div>
          </section>

        </article>
      </div>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="footer-container">
          <div className="footer-brand" data-testid="footer-brand">
            <span className="logo-text">Precision Prescribing</span>
            <p className="footer-tagline">Pharmacist-led prescribing capacity for regulated healthcare providers.</p>
          </div>
          <div className="footer-info" data-testid="footer-info">
            <p className="footer-legal-line">Precision Prescribing is a trading name of PHARMKG LTD.</p>
            <p>Company number: 14702569</p>
            <p>ICO registration: ZC107443</p>
            <p>Registered office: 261 High Street, Waltham Cross, EN8 7BE</p>
            <p>Email: <a href="mailto:hello@precisionpx.com">hello@precisionpx.com</a></p>
            <nav className="footer-links" data-testid="footer-links" aria-label="Legal">
              <a href="/privacy-policy" className="footer-link" data-testid="footer-privacy-link">Privacy Policy</a>
              <span className="footer-sep" aria-hidden="true">|</span>
              <a href="/cookie-policy" className="footer-link" data-testid="footer-cookie-link">Cookie Policy</a>
              <span className="footer-sep" aria-hidden="true">|</span>
              <a href="/terms-of-use" className="footer-link" data-testid="footer-terms-link">Website Terms of Use</a>
              <span className="footer-sep" aria-hidden="true">|</span>
              <button
                onClick={() => navigate("/", { state: { scrollTo: "contact" } })}
                className="footer-link"
                data-testid="footer-contact-link"
              >
                Contact
              </button>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CookiePolicy;
